/**
 * ObondoClientBillingCard — Section B2 (client-facing Obondo Dashboard)
 * for the Obondo Metered Billing Engine. Lives inside AgencyDashboard's
 * "API Billing" tab for agencies; a solo trader reaches the same backend
 * (`/obondo/mau/my-client`) via the standalone `/app/obondo-billing` route.
 *
 * Self-provisions on first load — no admin action required to "become an
 * API client" (Part 3's real deposit flow will later set the true tier;
 * until then everything runs on the Standard-tier test defaults + 7-day
 * trial from Section A).
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { KeyRound, Copy, Trash2, TrendingUp, Gauge, Wallet, CreditCard, CheckCircle2, XCircle } from "lucide-react";

function usd(n) {
    if (n == null) return "—";
    return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const TOKEN_STATE_STYLE = {
    normal: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
    alert: "bg-amber-500/15 text-amber-500 border-amber-500/30",
    throttle: "bg-orange-500/15 text-orange-500 border-orange-500/30",
    suspended: "bg-red-500/15 text-red-500 border-red-500/30",
};

export default function ObondoClientBillingCard() {
    const [snap, setSnap] = useState(null);
    const [traders, setTraders] = useState([]);
    const [search, setSearch] = useState("");
    const [conversion, setConversion] = useState(null);
    const [keys, setKeys] = useState([]);
    const [busy, setBusy] = useState(false);
    const [newKeyEnv, setNewKeyEnv] = useState("production");
    const [revealedKey, setRevealedKey] = useState(null);
    const [paymentOptions, setPaymentOptions] = useState(null);
    const [checkoutBusy, setCheckoutBusy] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [pollStatus, setPollStatus] = useState(null); // 'pending' | 'paid' | 'cancelled' | 'timeout'

    const load = useCallback(async () => {
        const [{ data: s }, { data: t }, { data: c }, { data: k }] = await Promise.all([
            api.get("/obondo/mau/my-client"),
            api.get("/obondo/mau/my-client/traders"),
            api.get("/obondo/mau/my-client/nudge-conversion"),
            api.get("/obondo/mau/my-client/api-keys"),
        ]);
        setSnap(s); setTraders(t.traders || []); setConversion(c); setKeys(k.keys || []);
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        api.get("/obondo/mau/my-client/payment-options").then(({ data }) => setPaymentOptions(data)).catch(() => {});
    }, []);

    // Post-return handling — if ?stripe_session_id is present, poll status.
    useEffect(() => {
        const sessionId = searchParams.get("stripe_session_id");
        const cancelled = searchParams.get("stripe_cancelled");
        if (cancelled) { setPollStatus("cancelled"); return; }
        if (!sessionId) return;

        setPollStatus("pending");
        let stopped = false;
        let attempts = 0;
        async function poll() {
            while (!stopped && attempts < 30) {   // ~60 s max
                attempts++;
                try {
                    const { data } = await api.get(`/stripe/status/${sessionId}`);
                    if (data.payment_status === "paid") {
                        setPollStatus("paid");
                        toast.success("Payment confirmed — your balance/slots have been updated.");
                        setSearchParams({});
                        load();
                        return;
                    }
                } catch (_e) { /* silent — poll again */ }
                await new Promise((r) => setTimeout(r, 2000));
            }
            if (!stopped) setPollStatus("timeout");
        }
        poll();
        return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    async function buySlots(packageKey) {
        setCheckoutBusy(`slots-${packageKey}`);
        try {
            const { data } = await api.post("/obondo/mau/my-client/checkout/slots", {
                package_key: packageKey,
                origin_url: window.location.origin + window.location.pathname,
            });
            if (data.checkout_url) window.location.href = data.checkout_url;
        } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); }
        finally { setCheckoutBusy(null); }
    }

    async function topUpBuffer(amount) {
        setCheckoutBusy(`buffer-${amount}`);
        try {
            const { data } = await api.post("/obondo/mau/my-client/checkout/buffer", {
                amount_usd: amount,
                origin_url: window.location.origin + window.location.pathname,
            });
            if (data.checkout_url) window.location.href = data.checkout_url;
        } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); }
        finally { setCheckoutBusy(null); }
    }

    async function saveConfig(patch) {
        setBusy(true);
        try {
            const { data } = await api.patch("/obondo/mau/my-client/config", patch);
            setSnap(data);
            toast.success("Preference saved");
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
        finally { setBusy(false); }
    }

    async function generateKey() {
        setBusy(true);
        try {
            const { data } = await api.post("/obondo/mau/my-client/api-keys", { environment: newKeyEnv });
            setRevealedKey(data.raw_key);
            const { data: k } = await api.get("/obondo/mau/my-client/api-keys");
            setKeys(k.keys || []);
        } catch (e) { toast.error(e.response?.data?.detail || "Key generation failed"); }
        finally { setBusy(false); }
    }

    async function revokeKey(id) {
        setBusy(true);
        try {
            await api.delete(`/obondo/mau/my-client/api-keys/${id}`);
            const { data: k } = await api.get("/obondo/mau/my-client/api-keys");
            setKeys(k.keys || []);
            toast.success("Key revoked");
        } catch (e) { toast.error("Revoke failed"); }
        finally { setBusy(false); }
    }

    if (!snap) return <div className="text-sm text-muted-foreground p-4">Loading your API billing profile…</div>;

    const slotsUsedPct = snap.prepaid_slots > 0 ? Math.min(100, (snap.total_billed_mau_this_cycle / snap.prepaid_slots) * 100) : 0;
    const filteredTraders = traders.filter((t) => t.trader_ref.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="space-y-6" data-testid="obondo-client-billing-card">
            {snap.token_state !== "normal" && (
                <div className={`border rounded p-3 text-sm font-semibold ${TOKEN_STATE_STYLE[snap.token_state]}`} data-testid="obondo-token-banner">
                    Cash buffer at {snap.cash_buffer_pct_remaining}% — reload funds to avoid service interruption.
                </div>
            )}

            {/* Top row — 4 widgets */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="border border-border bg-card rounded p-4" data-testid="obondo-stat-slots">
                    <div className="text-[11px] uppercase text-muted-foreground flex items-center gap-1"><Gauge className="w-3.5 h-3.5" /> Active slots remaining</div>
                    <div className="text-2xl font-black mt-1">{snap.remaining_slots}/{snap.prepaid_slots}</div>
                    <Progress value={100 - slotsUsedPct} className="h-1.5 mt-2" />
                    <div className="text-[11px] text-muted-foreground mt-1">Resets in {snap.days_remaining_in_cycle}d {snap.hours_remaining_in_cycle}h</div>
                </div>
                <div className={`border border-border bg-card rounded p-4 ${snap.cash_buffer_usd < 20 ? "animate-pulse border-amber-500/50" : ""}`} data-testid="obondo-stat-buffer">
                    <div className="text-[11px] uppercase text-muted-foreground flex items-center gap-1"><Wallet className="w-3.5 h-3.5" /> Cash buffer balance</div>
                    <div className={`text-2xl font-black mt-1 ${snap.cash_buffer_usd < 20 ? "text-amber-500" : ""}`}>{usd(snap.cash_buffer_usd)}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">{snap.cash_buffer_pct_remaining}% of last top-up</div>
                </div>
                <div className="border border-border bg-card rounded p-4" data-testid="obondo-stat-tier">
                    <div className="text-[11px] uppercase text-muted-foreground">Current tier status</div>
                    <Badge className="mt-1.5 text-sm">{snap.tier_label} (${snap.price_per_user_usd}/user)</Badge>
                    <div className="text-[11px] text-muted-foreground mt-1.5">{snap.trial_active ? "7-day trial — $0 billed" : "Billing active"}</div>
                </div>
                <div className="border border-border bg-card rounded p-4" data-testid="obondo-stat-overages">
                    <div className="text-[11px] uppercase text-muted-foreground">Active overages</div>
                    <div className="text-2xl font-black mt-1">{snap.active_overages_count} <span className="text-sm font-normal text-muted-foreground">users</span></div>
                    <div className="text-[11px] text-muted-foreground mt-1">Beyond 1,000 req/user this cycle</div>
                </div>
            </div>

            {pollStatus && (
                <div className={`border rounded p-3 text-sm flex items-center gap-2 ${
                    pollStatus === "paid" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-500" :
                    pollStatus === "cancelled" ? "bg-muted/40 border-border text-muted-foreground" :
                    "bg-amber-500/10 border-amber-500/30 text-amber-500"
                }`} data-testid="obondo-payment-status-banner">
                    {pollStatus === "paid" && <><CheckCircle2 className="w-4 h-4" /> Payment confirmed — your balance/slots have been updated.</>}
                    {pollStatus === "cancelled" && <><XCircle className="w-4 h-4" /> Checkout cancelled — no charge made.</>}
                    {pollStatus === "pending" && "Awaiting Stripe confirmation…"}
                    {pollStatus === "timeout" && "Still processing — refresh in a moment if your balance hasn't updated."}
                </div>
            )}

            {/* Part 3 — Prepaid Slots + Cash Buffer payment gateway */}
            {paymentOptions && (
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="border border-border bg-card rounded p-4" data-testid="obondo-buy-slots">
                        <div className="overline mb-2 flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Buy Prepaid Slots</div>
                        <div className="text-[11px] text-muted-foreground mb-3">One-time purchase — adds slots immediately, on top of your current allocation.</div>
                        <div className="grid grid-cols-3 gap-2">
                            {paymentOptions.slot_packages.map((p) => (
                                <Button key={p.key} variant="outline" size="sm" className="flex flex-col h-auto py-2"
                                    onClick={() => buySlots(p.key)} disabled={checkoutBusy === `slots-${p.key}`}
                                    data-testid={`obondo-buy-slots-${p.key}`}>
                                    <span className="font-bold text-sm">{usd(p.amount_usd)}</span>
                                    <span className="text-[10px] text-muted-foreground">{p.slots} slots</span>
                                </Button>
                            ))}
                        </div>
                    </div>
                    <div className="border border-border bg-card rounded p-4" data-testid="obondo-topup-buffer">
                        <div className="overline mb-2 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Top Up Cash Buffer</div>
                        <div className="text-[11px] text-muted-foreground mb-3">One-time card top-up — funds are added instantly on payment confirmation.</div>
                        <div className="grid grid-cols-4 gap-2">
                            {paymentOptions.buffer_topup_amounts_usd.map((amount) => (
                                <Button key={amount} variant="outline" size="sm"
                                    onClick={() => topUpBuffer(amount)} disabled={checkoutBusy === `buffer-${amount}`}
                                    data-testid={`obondo-topup-buffer-${amount}`}>
                                    {usd(amount)}
                                </Button>
                            ))}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-3">Test card: <span className="mono">4242 4242 4242 4242</span> · any future expiry · any CVC.</div>
                    </div>
                </div>
            )}

            {/* MAU Meter & Billing Predictor + Burn-down */}
            <div className="grid md:grid-cols-2 gap-4">
                <div className="border border-border bg-card rounded p-4" data-testid="obondo-mau-meter">
                    <div className="overline mb-2 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Real-Time MAU Meter & Billing Predictor</div>
                    <div className="text-3xl font-black">{snap.total_billed_mau_this_cycle}</div>
                    <div className="text-xs text-muted-foreground mb-2">unique user IDs active this cycle (excludes signups who never triggered the API)</div>
                    <div className="text-sm flex justify-between border-t border-border/50 pt-2">
                        <span className="text-muted-foreground">Linear projection @ cycle end</span>
                        <span className="font-semibold">{snap.projected_mau_end_of_cycle} MAUs</span>
                    </div>
                    <div className="text-sm flex justify-between">
                        <span className="text-muted-foreground">Projected invoice</span>
                        <span className="font-bold">{usd(snap.projected_invoice_usd)}</span>
                    </div>
                </div>
                <div className="border border-border bg-card rounded p-4" data-testid="obondo-burndown">
                    <div className="overline mb-2">Real-Time Balance Burn-Down</div>
                    <div className="text-xs text-muted-foreground mb-2">Drops by 1 the moment a NEW user hits the API — repeat hits from the same user never move it again.</div>
                    <div className="flex items-center gap-3">
                        <div className="text-3xl font-black">{snap.remaining_slots}</div>
                        <div className="flex-1">
                            <Progress value={100 - slotsUsedPct} className="h-3" />
                        </div>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">{snap.total_billed_mau_this_cycle} of {snap.prepaid_slots} slots consumed</div>
                </div>
            </div>

            {/* Nudge conversion analytics */}
            <div className="border border-border bg-card rounded p-4" data-testid="obondo-nudge-conversion">
                <div className="overline mb-2">Behavioral Nudge Conversion Analytics</div>
                {conversion && (
                    <div className="grid grid-cols-3 gap-3 text-center">
                        <div><div className="text-2xl font-black">{conversion.ideas_pushed_this_cycle}</div><div className="text-[11px] text-muted-foreground">Ideas pushed</div></div>
                        <div><div className="text-2xl font-black">{conversion.trades_made_this_cycle}</div><div className="text-[11px] text-muted-foreground">Trades made</div></div>
                        <div><div className="text-2xl font-black text-emerald-500">{conversion.conversion_pct ?? "—"}%</div><div className="text-[11px] text-muted-foreground">Conversion (proxy)</div></div>
                    </div>
                )}
                <div className="text-[11px] text-muted-foreground mt-2">Aggregate proxy across all traders this cycle — not a per-idea click-through link.</div>
            </div>

            {/* Unique trader ID logs — searchable */}
            <div className="border border-border bg-card rounded p-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="overline">Unique Trader ID Logs</div>
                    <Input placeholder="Search trader ref…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-[200px] h-8" data-testid="obondo-trader-search" />
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="obondo-traders-table">
                        <thead><tr className="text-left text-muted-foreground border-b border-border">
                            <th className="py-1.5 pr-3">Anonymized ID</th><th className="pr-3">Activated</th>
                            <th className="pr-3">Requests this month</th><th className="pr-3">Overage cost</th><th>Status</th>
                        </tr></thead>
                        <tbody>
                            {filteredTraders.length === 0 && <tr><td colSpan={5} className="py-3 text-center text-muted-foreground">No matching traders.</td></tr>}
                            {filteredTraders.map((t) => (
                                <tr key={t.trader_ref} className="border-b border-border/50">
                                    <td className="py-1.5 pr-3 mono">{t.trader_ref}</td>
                                    <td className="pr-3">{t.billed_at ? new Date(t.billed_at).toLocaleDateString() : "—"}</td>
                                    <td className="pr-3">
                                        <span className={t.api_requests_this_cycle >= 950 && t.api_requests_this_cycle < 1000 ? "text-amber-500 font-semibold" : ""}>{t.api_requests_this_cycle}</span> / {t.fair_use_cap}
                                    </td>
                                    <td className="pr-3">{usd(t.overage_cost_usd)}</td>
                                    <td>
                                        {t.status === "overage" && <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/30">Overage [+$0.005/req]</Badge>}
                                        {t.status === "normal" && <Badge variant="outline">Normal</Badge>}
                                        {t.status === "not_activated" && <Badge variant="secondary">Throttled</Badge>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Self-service API keys */}
            <div className="border border-border bg-card rounded p-4">
                <div className="overline mb-2 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Self-Service API Key Management</div>
                <div className="flex items-center gap-2 mb-3">
                    <Select value={newKeyEnv} onValueChange={setNewKeyEnv}>
                        <SelectTrigger className="w-[140px]" data-testid="obondo-key-env-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="production">Production</SelectItem>
                            <SelectItem value="sandbox">Sandbox</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button size="sm" onClick={generateKey} disabled={busy} data-testid="obondo-generate-key-btn">Generate key</Button>
                </div>
                {revealedKey && (
                    <div className="border border-amber-500/40 bg-amber-500/10 rounded p-2 mb-3 flex items-center justify-between gap-2" data-testid="obondo-revealed-key">
                        <code className="text-xs break-all">{revealedKey}</code>
                        <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard?.writeText(revealedKey); toast.success("Copied"); }}><Copy className="w-3.5 h-3.5" /></Button>
                    </div>
                )}
                {revealedKey && <div className="text-[11px] text-amber-500 mb-3">Copy this now — it won't be shown again.</div>}
                <div className="space-y-1.5">
                    {keys.length === 0 && <div className="text-xs text-muted-foreground">No keys generated yet.</div>}
                    {keys.map((k) => (
                        <div key={k.id} className="flex items-center justify-between text-xs border border-border rounded px-2.5 py-1.5">
                            <span className="mono">{k.key_prefix}••••••••</span>
                            <Badge variant={k.environment === "production" ? "default" : "secondary"}>{k.environment}</Badge>
                            {k.active ? (
                                <Button size="sm" variant="ghost" onClick={() => revokeKey(k.id)} data-testid={`obondo-revoke-key-${k.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                            ) : <Badge variant="outline">Revoked</Badge>}
                        </div>
                    ))}
                </div>
            </div>

            {/* Feed cost vs margin (client's own copy) */}
            <div className="border border-border bg-card rounded p-4">
                <div className="overline mb-2">Feed Cost vs Margin (your account)</div>
                <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Your MAU billing (est.)</span><span className="font-semibold">{usd(snap.estimated_invoice_usd)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Live feed cost attributable to you (est.)</span><span className="font-semibold">{usd(snap.feed_cost_usd_this_cycle)}</span></div>
                </div>
            </div>

            {/* Config panels */}
            <div className="grid md:grid-cols-2 gap-4">
                <ConfigPanel title="Auto-Refill Threshold (Cash Buffer)" testId="obondo-cfg-autorefill">
                    <ConfigRow label="Enabled" testId="obondo-cfg-autorefill-toggle">
                        <Switch checked={snap.config.auto_refill_enabled} onCheckedChange={(v) => saveConfig({ auto_refill_enabled: v })} />
                    </ConfigRow>
                    <NumberField label="When buffer drops below ($)" value={snap.config.auto_refill_threshold_usd} onSave={(v) => saveConfig({ auto_refill_threshold_usd: v })} testId="obondo-cfg-autorefill-threshold" />
                    <NumberField label="Charge saved card for ($)" value={snap.config.auto_refill_amount_usd} onSave={(v) => saveConfig({ auto_refill_amount_usd: v })} testId="obondo-cfg-autorefill-amount" />
                </ConfigPanel>

                <ConfigPanel title="Overage Hard Cap" testId="obondo-cfg-hardcap">
                    <ConfigRow label="Enabled" testId="obondo-cfg-hardcap-toggle">
                        <Switch checked={snap.config.overage_hard_cap_enabled} onCheckedChange={(v) => saveConfig({ overage_hard_cap_enabled: v })} />
                    </ConfigRow>
                    <NumberField label="Cap total monthly overage spend at ($)" value={snap.config.overage_hard_cap_usd} onSave={(v) => saveConfig({ overage_hard_cap_usd: v })} testId="obondo-cfg-hardcap-amount" />
                    <div className="text-[11px] text-muted-foreground">Once reached, overage users are blocked (429) instead of draining your buffer.</div>
                </ConfigPanel>

                <ConfigPanel title="User Provisioning Rule" testId="obondo-cfg-provisioning">
                    <ConfigRow label="Auto-activate a new slot for a brand-new trader mid-cycle" testId="obondo-cfg-provisioning-toggle">
                        <Switch checked={snap.config.auto_provision_new_users} onCheckedChange={(v) => saveConfig({ auto_provision_new_users: v })} />
                    </ConfigRow>
                    <div className="text-[11px] text-muted-foreground">Off = new users beyond your slot count are held for admin authorization instead of billed automatically.</div>
                </ConfigPanel>

                <ConfigPanel title="Auto-Top-Up Trigger (Prepaid Slots)" testId="obondo-cfg-autotopup-slots">
                    <ConfigRow label="Enabled" testId="obondo-cfg-autotopup-toggle">
                        <Switch checked={snap.config.auto_topup_slots_enabled} onCheckedChange={(v) => saveConfig({ auto_topup_slots_enabled: v })} />
                    </ConfigRow>
                    <NumberField label="If slots remaining drops below (%)" value={snap.config.auto_topup_slots_threshold_pct} onSave={(v) => saveConfig({ auto_topup_slots_threshold_pct: v })} testId="obondo-cfg-autotopup-threshold" />
                    <div className="text-[11px] text-muted-foreground">Automatically buys another batch of slots at your current tier rate.</div>
                </ConfigPanel>
            </div>
        </div>
    );
}

function ConfigPanel({ title, children, testId }) {
    return (
        <div className="border border-border bg-card rounded p-4 space-y-2" data-testid={testId}>
            <div className="overline">{title}</div>
            {children}
        </div>
    );
}

function ConfigRow({ label, children, testId }) {
    return (
        <div className="flex items-center justify-between" data-testid={testId}>
            <Label className="text-sm">{label}</Label>
            {children}
        </div>
    );
}

function NumberField({ label, value, onSave, testId }) {
    const [val, setVal] = useState(value ?? "");
    useEffect(() => { setVal(value ?? ""); }, [value]);
    return (
        <div className="flex items-center justify-between gap-2">
            <Label className="text-xs text-muted-foreground flex-1">{label}</Label>
            <Input type="number" value={val} onChange={(e) => setVal(e.target.value)}
                onBlur={() => { if (val !== "" && Number(val) !== value) onSave(Number(val)); }}
                className="w-[100px] h-8 text-xs" data-testid={testId} />
        </div>
    );
}
